import React from 'react';
import AuthAdminUserModel from '../../code/models/AuthAdminUserModel';
import CclDataTable, { CclDataTableColumnDef, CclDataTableColumnType } from '../CclDataTable';
import { CloseButton, Input } from 'reactstrap';
import ApiWaive from '../../code/api/ApiWaive';
import Waive from '../../code/models/Waive';
import '../../css/adminwaiveform.css';
import Toaster from "../Toaster";

interface IAdminWaivesForm {
    adminUser: AuthAdminUserModel;
}

export default function AdminWaiveForm({ adminUser }: IAdminWaivesForm) {
    const [toastMessage, setToastMessage] = React.useState<string>("");
    const [allWaives, setAllWaives] = React.useState<Waive[] | null>(null);
    const [filteredWaives, setFilteredWaives] = React.useState<Waive[] | null>(null);
    const [selectedWaive, setSelectedWaive] = React.useState<Waive | null>(null);
    const [editWaive, setEditWaive] = React.useState<Waive | null>(null);
    const [anyChanges, setAnyChanges] = React.useState<boolean>(false);
    const [jsonInvalid, setJsonInvalid] = React.useState(false);

    React.useEffect(() => {
        loadWaives();
    }, []);

    async function loadWaives() {
        ApiWaive.getWaives().then(data => {
            var sortedData = data.sort((a, b) => {
                var sorta = `${a?.waiveType}${a.code}${a?.name}`;
                var sortb = `${b?.waiveType}${b.code}${b?.name}`;
                return sorta.localeCompare(sortb);
            });

            setAllWaives(sortedData);
            setFilteredWaives(sortedData);
        });
    }

    function reloadWaives() {
        setAllWaives(null);
        setFilteredWaives(null);
        loadWaives();
    }

    function saveWaive() {
        if (anyChanges && !!selectedWaive && !!editWaive) {
            var isNewItem = (editWaive.waiveId || 0) <= 0;

            if (isNewItem && !!allWaives) {
                var newId = 1 + Math.max(...allWaives.map((og) => { return og.waiveId; }));
                editWaive.waiveId = newId;
            }

            setToastMessage("");

            ApiWaive.updateWaive(editWaive).then(data => {
                if (!!data) {
                    selectedWaive.waiveId = data.waiveId;
                    selectedWaive.code = data.code;
                    selectedWaive.name = data.name;
                    selectedWaive.description = data.description;
                    selectedWaive.waiveType = data.waiveType;
                    selectedWaive.parentWaiveId = data.parentWaiveId;
                    selectedWaive.summaryPosition = data.summaryPosition;
                    selectedWaive.configJson = data.configJson;

                    if (isNewItem) {
                        allWaives?.push(selectedWaive);
                        filteredWaives?.push(selectedWaive);
                    }

                    handleRowSelect(selectedWaive);
                    setToastMessage(`${isNewItem ? "New " : ""}Waive '${selectedWaive.name}' Saved.`);
                }
            });
        }
    }

    function handleAddNewClick() {
        var newItem = new Waive();

        newItem.waiveId = 0;
        newItem.name = "";
        newItem.code = "";
        newItem.description = "";
        newItem.waiveType = "line";
        newItem.parentWaiveId = null;
        newItem.summaryPosition = "top";
        newItem.configJson = "{}";

        handleRowSelect(newItem);
    }

    function handleRowSelect(rowData: Waive) {
        setSelectedWaive(rowData);
        setEditWaive(structuredClone(rowData));
        setAnyChanges(false);
    }

    function closeEditor() {
        setEditWaive(null);
    }

    function checkEditWaiveHasChanges() {
        var isAnyChanges = editWaive !== null &&
            (selectedWaive?.waiveId !== editWaive?.waiveId ||
                selectedWaive?.name !== editWaive?.name ||
                selectedWaive?.code !== editWaive?.code ||
                selectedWaive?.description !== editWaive?.description ||
                selectedWaive?.waiveType !== editWaive?.waiveType ||
                selectedWaive?.parentWaiveId !== editWaive?.parentWaiveId ||
                selectedWaive?.summaryPosition !== editWaive?.summaryPosition ||
                selectedWaive?.configJson !== editWaive?.configJson);
        setAnyChanges(isAnyChanges);
    }

    function getButtonStateClass(isActive: Boolean, additionalClasses: string) {
        const classnames = ["btn"];
        const mainStyle = isActive ? "btn-primary" : "btn-outline-primary";
        classnames.push(mainStyle);

        if (!!additionalClasses) {
            classnames.push(additionalClasses);
        }

        return classnames.join(" ");
    }


    function formatParent(val: any) {
        var result = allWaives?.find((waive) => waive.waiveId === val);
        return result?.code;
    }

    const profileColumnDefs: CclDataTableColumnDef[] = [
        {
            fieldName: "waiveType",
            title: "Type",
            visible: true,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "code",
            title: "Code",
            visible: true,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "parentWaiveId",
            title: "Parent",
            visible: true,
            formatData: formatParent,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "name",
            title: "Name",
            visible: true,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "description",
            title: "Description",
            visible: true,
            type: CclDataTableColumnType.Data
        },
        {
            fieldName: "summaryPosition",
            title: "Position",
            visible: true,
            type: CclDataTableColumnType.Data
        }
    ];


    function validateJson(jsonData: string): boolean {
        var isInValid: boolean;
        try {
            JSON.parse(jsonData);
            isInValid = false;
        } catch (error) {
            isInValid = true;
        }
        if (jsonInvalid != isInValid) {
            setJsonInvalid(isInValid);
        }
        return !isInValid;
    }

    function prettyJson(jsonData: string): string {
        try {
            var data = JSON.parse(jsonData);
            return JSON.stringify(data, null, 2);
        }
        catch {
            return jsonData;
        }
    }

    function minifyJson(jsonData: string): string {
        try {
            var data = JSON.parse(jsonData);
            return JSON.stringify(data);
        } catch {
            return jsonData;
        }
    }

    function handleSearchWaive(searchValue: string) {
        if (!allWaives) return;

        if (!searchValue) {
            setFilteredWaives([...allWaives]);
        } else {
            const newFilterData = allWaives.filter(c => {
                return (`${c.waiveType}~${c.code}~${c.name}~${c.description}`)
                    .toLowerCase().includes(searchValue.toLowerCase());
            });
            setFilteredWaives(newFilterData);
        }
    }

    function buildParentWaiveOptions() {
        return allWaives?.map((waive) => {
            if (waive.waiveType === selectedWaive?.waiveType && waive.waiveId !== selectedWaive?.waiveId) {
                return (<option value={waive.waiveId}>{waive.name}</option>)
            }
        }
        );
    }

    function buildParentSelector() {
        if (!editWaive) return null;
        return (
            <div className="waive-parent">
                <label htmlFor="editParentWaive" className="form-label">Parent</label>
                <select className="form-select" id="editParentWaive"
                    defaultValue={`${selectedWaive?.parentWaiveId}`}
                    autoComplete={"off"} onChange={(e) => {
                        if (!!e.target.value) {
                            editWaive.parentWaiveId = parseInt(e.target.value, 10);
                        } else {
                            editWaive.parentWaiveId = null;
                        }
                        checkEditWaiveHasChanges();
                    }}>
                    <option value={undefined}>None</option>
                    {buildParentWaiveOptions()}
                </select>
            </div>
        )
    }

    function buildWaiveEditor() {
        return (<React.Fragment>
            {!!editWaive && !!selectedWaive &&
                <div key={"wv" + selectedWaive.waiveId} className='waives-form'>
                    <div className='waives-editors'>
                        <div className='waives-editors-row'>
                            <div className="waive-type">
                                <label htmlFor="editWaiveType" className="form-label">Type</label>
                                <select className="form-select" id="editWaiveType"
                                    defaultValue={`${selectedWaive.waiveType}`}
                                    autoComplete={"off"} onChange={(e) => {
                                        editWaive.waiveType = e.target.value;
                                        checkEditWaiveHasChanges();
                                    }}>
                                    <option value={"line"}>Line</option>
                                    <option value={"reserve"}>Reserve</option>
                                    <option value={"np-reserve"}>Non-profile Reserve</option>
                                </select>
                            </div>
                            <div className="waive-code">
                                <label htmlFor="editwaiveCode" className="form-label">Code</label>
                                <input className="form-control" id="editWaiveCode" defaultValue={selectedWaive.code}
                                    autoComplete={"off"} maxLength={10}
                                    onChange={(e) => {
                                        editWaive.code = e.target.value;
                                        checkEditWaiveHasChanges();
                                    }}></input>
                            </div>
                            {buildParentSelector()}
                            <div className="waive-name">
                                <label htmlFor="editWaiveName" className="form-label">Name</label>
                                <input className="form-control" id="editWaiveName" defaultValue={selectedWaive.name}
                                    maxLength={50} autoComplete={"off"}
                                    onChange={(e) => {
                                        editWaive.name = e.target.value;
                                        checkEditWaiveHasChanges();
                                    }}></input>
                            </div>
                        </div>

                        <div className='waives-editors-row'>
                            <div className="waive-description">
                                <label htmlFor="editWaiveDescription" className="form-label">Description</label>
                                <input className="form-control" id="editWaiveDescription"
                                    defaultValue={selectedWaive.description}
                                    autoComplete={"off"} maxLength={255}
                                    onChange={(e) => {
                                        editWaive.description = e.target.value;
                                        checkEditWaiveHasChanges();
                                    }}></input>
                            </div>
                            <div className="waive-summaryposition">
                                <label htmlFor="editWaiveSummaryPosition" className="form-label">Position</label>
                                <select className="form-select" id="editWaiveSummaryPosition"
                                    defaultValue={`${selectedWaive.summaryPosition}`}
                                    autoComplete={"off"} onChange={(e) => {
                                        editWaive.summaryPosition = e.target.value;
                                        checkEditWaiveHasChanges();
                                    }}>
                                    <option value={"top"}>Top</option>
                                    <option value={"bottom"}>Bottom</option>
                                    <option value={"anywhere"}>Anywhere</option>
                                </select>
                            </div>

                            <div className="waive-savebutton">
                                <button disabled={!anyChanges} className="btn btn-primary save-button"
                                    onClick={saveWaive}>Save Changes</button>
                            </div>
                            <div className="waive-closebutton">
                                <CloseButton onClick={closeEditor} />
                            </div>
                        </div>
                    </div>
                    <div className='waives-configjson'>
                        <label htmlFor="editWaiveConfigJson" className="form-label">Config Json{jsonInvalid && <span className="bad-json"> [Json Invalid]</span>}</label>
                        <Input className="form-control waives-configjson-editor" id="editWaiveConfigJson"
                            defaultValue={`${prettyJson(selectedWaive.configJson)}`}
                            type="textarea" autoComplete={"off"} onChange={(e) => {
                                if (validateJson(e.target.value)) {
                                    editWaive.configJson = minifyJson(e.target.value);
                                    checkEditWaiveHasChanges();
                                }
                            }}></Input>
                    </div>
                </div>}
        </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <div className='buttonbar'>
                <button disabled={!filteredWaives} className={getButtonStateClass(true, "")} onClick={reloadWaives}>Reload</button>
                <button disabled={!filteredWaives} className={getButtonStateClass(true, "")} onClick={handleAddNewClick}>Add New</button>
                <label htmlFor="searchWaives" className="form-label search-waives-label">Search</label>
                <input className="form-control search-waives" id="searchWaives" autoComplete={"off"} onChange={(e) => { handleSearchWaive(e.target.value); }}></input>
            </div>
            {buildWaiveEditor()}
            <div className='waives-table-wrapper'>
                <CclDataTable
                    name={"profilesTable"}
                    tableData={filteredWaives || []}
                    columnDefinitions={profileColumnDefs}
                    rowSelect={true}
                    onRowSelect={handleRowSelect}
                    onButtonClick={null} />
            </div>
            <Toaster title={"Waives"} subtitle={""} message={toastMessage} />
        </React.Fragment>
    )
}
